import { FC, useState } from "react";
import { Box, IconButton, Tooltip, Snackbar, useMediaQuery } from "@mui/material";
import { FiCopy, FiCheck } from "react-icons/fi";
import { MessageSender } from "@/types/chat";
import { trackFeedbackGiven } from "@/analytics/posthog-events";

interface MessageActionsProps {
  content: string;
  messageId: string;
  sender: MessageSender;
  showAlways?: boolean; // For mobile - always show actions
}

const MessageActions: FC<MessageActionsProps> = ({
  content,
  messageId,
  sender,
  showAlways = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const isMobileWidth = useMediaQuery('(max-width:600px)');
  const isAI = sender === MessageSender.AI;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setShowSnackbar(true);
      setTimeout(() => setCopied(false), 2000);
      trackFeedbackGiven(messageId, "copy");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = content;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setShowSnackbar(true);
        setTimeout(() => setCopied(false), 2000);
        trackFeedbackGiven(messageId, "copy");
      } catch {
        // ignore
      }
      document.body.removeChild(textArea);
    }
  };

  const iconSize = isMobileWidth ? 16 : 14;
  const buttonSize = isMobileWidth ? 32 : 28;

  return (
    <>
      <Box
        className="message-actions"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 0.5,
          opacity: showAlways || isMobileWidth ? 1 : 0,
          transition: "opacity 0.15s ease-in-out",
          justifyContent: isAI ? "flex-start" : "flex-end",
          ".chat-message-container:hover &": {
            opacity: 1,
          },
        }}
      >
        {/* Copy button - always available */}
        <Tooltip title={copied ? "Copied!" : "Copy message"}>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              width: buttonSize,
              height: buttonSize,
              color: copied ? "#43A047" : "#687071",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            {copied ? <FiCheck size={iconSize} /> : <FiCopy size={iconSize} />}
          </IconButton>
        </Tooltip>

      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            minWidth: "auto",
            padding: "6px 16px",
            backgroundColor: "#323232",
          },
        }}
      />
    </>
  );
};

export default MessageActions;
